import { useCallback, useMemo, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { Theme } from '@mui/material';

import { SpinWheelGame } from '@occc-app/design-system';
import { Random_Award_Type } from '@occc/graphql';
import { nativeGamePalettes } from '@occc-app/routes/Game/GameConfigureForm/constants';
import config from '@occc/config';

import { GameType, SegmentFormValues, SpinWheelGameOfferFormValues } from '../../types';

type SpinWheelGamePreviewProps = {
  theme: Theme;
  segments: SegmentFormValues[];
  config: SpinWheelGameOfferFormValues;
  gameplayAmountDisplayed: boolean;
  // It is better to use the ratio of 9:19.5
  cssWidth?: string;
  cssHeight?: string;
};

const SpinWheelGamePreview = (props: SpinWheelGamePreviewProps) => {
  const {
    theme,
    segments,
    config,
    gameplayAmountDisplayed,
    cssWidth = '400px',
    cssHeight = '800px',
  } = props;
  const [sectorIndex, setSectorIndex] = useState(-1);

  const play = useCallback(() => {
    setSectorIndex(segments.length ? Math.round(Math.random() * segments.length - 1) : -1);
  }, [segments.length]);

  return (
    <IntlProvider locale='en'>
      <ThemeProvider theme={theme}>
        <SpinWheelGame
          winningSectorIndex={sectorIndex}
          sectors={segments.map((segment) => ({
            id: segment.id,
            color: segment.color || undefined,
            type: segment.type,
            icon: segment.icon || undefined,
            points:
              segment.type === Random_Award_Type.Points && segment.awardPoints
                ? segment.awardPoints
                : undefined,
          }))}
          headerText={config.headerText}
          headerTextColor={config.headerTextColor || undefined}
          disclaimerTextColor={config.footerTextColor || undefined}
          backgroundPrimaryColor={config.backgroundPrimaryColor || undefined}
          backgroundSecondaryColor={config.backgroundSecondaryColor || undefined}
          backgroundImage={config.backgroundImage || undefined}
          foregroundImage={config.foregroundImage || undefined}
          borderColor={config.borderColor || undefined}
          buttonColor={config.buttonColor || undefined}
          buttonTextColor={config.buttonTextColor || undefined}
          cssWidth={cssWidth}
          cssHeight={cssHeight}
          numberOfGamePlays={gameplayAmountDisplayed ? 3 : undefined}
          isPlayButtonDisabled={false}
          isPlayButtonVisible={true}
          isPurchaseButtonLoading={false}
          isPurchaseButtonDisabled={true}
          isPurchaseButtonVisible={false}
          termsUrl='https://stuzo.com' // to show how looks this element, also i think that should be customized
          onPlayClick={play} // just to show button states, and to make it more funny
        />
      </ThemeProvider>
    </IntlProvider>
  );
};

type GamePreviewProps = {
  gameType: GameType;
  segments: SegmentFormValues[];
  spinWheelGameConfig: SpinWheelGameOfferFormValues | null;
  isGameplayAmountDisplayed: boolean;
  cssWidth?: string;
  cssHeight?: string;
};

export const GamePreview = (props: GamePreviewProps) => {
  const {
    isGameplayAmountDisplayed,
    gameType,
    segments,
    spinWheelGameConfig,
    cssWidth,
    cssHeight,
  } = props;

  const themeOrigin = useTheme();
  const theme = useMemo(
    () =>
      createTheme({
        ...themeOrigin,
        palette: { ...themeOrigin.palette, ...nativeGamePalettes[config.stage] },
      }),
    [themeOrigin],
  );

  if (gameType === GameType.SPIN_THE_WHEEL && spinWheelGameConfig !== null) {
    return (
      <SpinWheelGamePreview
        theme={theme}
        segments={segments}
        config={spinWheelGameConfig}
        gameplayAmountDisplayed={isGameplayAmountDisplayed}
        cssWidth={cssWidth}
        cssHeight={cssHeight}
      />
    );
  }
  // Add more game types here
  return null;
};
